import { cn } from '@/lib/utils'

/**
 * The application's segmented toggle — the same shape as CardTableViewToggle, generalised.
 *
 * NOT A ROW OF BUTTONS. A `variant="default"` button for the selected option paints it in the
 * primary colour, which reads as an action to take rather than a state you are in, and it competes
 * with everything else on the screen that is genuinely blue. Here the selected segment is simply
 * lifted out of the track with the page background and a shadow.
 *
 * EVERY SEGMENT IS THE SAME HEIGHT AND PADDING, selected or not. When only the selected one grew a
 * border or heavier weight, choosing "Full breakdown" made the whole row taller and the content
 * below jumped — which is what made it feel broken rather than merely inconsistent.
 */
export interface SegmentedOption<T extends string> {
  value: T
  label: string
  /** A segment that cannot be chosen yet. Kept visible, because hiding it would leave no clue
   *  the capability exists; the title says what would make it available. */
  disabled?: boolean
  title?: string
}

export function SegmentedControl<T extends string>({
  value, onValueChange, options, className, size = 'default', ariaLabel,
}: {
  value: T
  onValueChange: (value: T) => void
  options: SegmentedOption<T>[]
  className?: string
  size?: 'default' | 'sm'
  ariaLabel?: string
}) {
  return (
    <div role="tablist" aria-label={ariaLabel}
         className={cn('inline-flex gap-1 rounded-lg border bg-muted/30 p-0.5', className)}>
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          role="tab"
          aria-selected={value === o.value}
          aria-disabled={o.disabled || undefined}
          disabled={o.disabled}
          title={o.title}
          onClick={() => { if (!o.disabled) onValueChange(o.value) }}
          className={cn(
            'rounded-md font-medium transition-colors whitespace-nowrap',
            size === 'sm' ? 'px-2.5 py-1 text-xs' : 'px-4 py-1.5 text-sm',
            o.disabled
              ? 'text-muted-foreground/40 cursor-not-allowed'
              : value === o.value
                ? 'bg-background shadow-sm'
                : 'text-muted-foreground hover:text-foreground',
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  )
}
